import { ClubPastelPalette, Colors } from '../constants/colors';

/**
 * Simple LRU (Least Recently Used) Cache
 * Stores a maximum of `capacity` items, evicting oldest when full
 */
class LRUCache<K, V> {
  private cache: Map<K, V>;
  private capacity: number;

  constructor(capacity: number) {
    this.cache = new Map();
    this.capacity = capacity;
  }

  get(key: K): V | undefined {
    if (!this.cache.has(key)) {
      return undefined;
    }

    // Move to end (most recently used)
    const value = this.cache.get(key)!;
    this.cache.delete(key);
    this.cache.set(key, value);
    return value;
  }

  set(key: K, value: V): void {
    // Delete if exists (to update position)
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }

    // Add to end
    this.cache.set(key, value);

    // Evict oldest if over capacity
    if (this.cache.size > this.capacity) {
      const firstKey = this.cache.keys().next().value!;
      this.cache.delete(firstKey);
    }
  }

  clear(): void {
    this.cache.clear();
  }
}

/**
 * Club Colors Service
 *
 * Provides deterministic color assignment for clubs based on their name.
 * Uses cyrb53 hash for even distribution across the pastel palette.
 * Caches computed colors for performance.
 */
class ClubColorsService {
  private colorCache: LRUCache<string, string>;

  constructor() {
    // Cache up to 100 club colors
    this.colorCache = new LRUCache(100);
  }

  /**
   * cyrb53 hash function - fast, simple string hash with avalanche properties
   * Source: https://github.com/bryc/code/blob/master/jshash/experimental/cyrb53.js
   *
   * @param str String to hash (club name)
   * @param seed Hash seed (default: 0)
   * @returns 53-bit hash as number
   */
  private cyrb53(str: string, seed: number = 0): number {
    let h1 = 0xdeadbeef ^ seed;
    let h2 = 0x41c6ce57 ^ seed;

    for (let i = 0, ch; i < str.length; i++) {
      ch = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }

    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
  }

  /**
   * Get deterministic color for a club based on its name
   *
   * @param clubName Name of the club
   * @returns Hex color from pastel palette
   */
  getClubColor(clubName: string): string {
    if (!clubName || clubName.trim() === '') {
      // Fallback to brand primary for empty names
      return Colors.primary;
    }

    // Normalize name: trim and lowercase for consistency
    const normalizedName = clubName.trim().toLowerCase();

    // Check cache first
    const cached = this.colorCache.get(normalizedName);
    if (cached) {
      return cached;
    }

    // Hash to palette index
    const hash = this.cyrb53(normalizedName);
    const paletteIndex = hash % ClubPastelPalette.length;
    const color = ClubPastelPalette[paletteIndex];

    // Cache result
    this.colorCache.set(normalizedName, color);

    return color;
  }

  /**
   * Clear color cache (useful for testing or memory management)
   */
  clearCache(): void {
    this.colorCache.clear();
  }
}

// Export singleton instance
export const clubColorsService = new ClubColorsService();
